import { FC, useMemo } from 'react';
import { Button, Col, Row, Tabs, Typography } from 'antd';
import { TbCode, TbDatabase, TbFileText, TbSettings, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SetStateMethod } from '@/types';

import type {
    VantageDataBlock,
    VantageDisplayMode,
    VantageEditorPreferences,
    VantageMetadata,
    VantageQueryHistoryEntry,
    VantageQueryResult,
    VantageValidationResult,
    VantageVisualizationType,
} from '../../../types';
import QueryMetadataTab from './components/MetadataTab';
import PreferencesTab from './components/PreferencesTab';
import QueriesTab from './components/QueriesTab';
import QueryTemplatesTab from './components/QueryTemplatesTab';

interface SettingsProps {
    setOpen: SetStateMethod<boolean>;
    code: string;
    setCode: SetStateMethod<string>;
    visualization: VantageVisualizationType;
    setVisualization: SetStateMethod<VantageVisualizationType>;
    displayMode: VantageDisplayMode;
    setDisplayMode: SetStateMethod<VantageDisplayMode>;
    limit: number;
    setLimit: SetStateMethod<number>;
    preferences: VantageEditorPreferences;
    setPreferences: SetStateMethod<VantageEditorPreferences>;
    metadata: VantageMetadata;
    metadataError: string | null;
    validation: VantageValidationResult | null;
    result: VantageQueryResult | null;
    runtimeMs: number | null;
    error: string | null;
    runQuery: () => void;
    insertAtCursor: (value: string) => void;
    dataBlocks: VantageDataBlock[];
    deleteDataBlock: (dataBlockId: string) => Promise<void>;
    updateDataBlockReferenceName: (dataBlockId: string, referenceName: string) => Promise<void>;
    getDataBlockReferenceNameError: (dataBlockId: string, referenceName: string) => string | undefined;
    queryHistory: VantageQueryHistoryEntry[];
}

const Settings: FC<SettingsProps> = ({
    setOpen,
    code,
    setCode,
    visualization,
    setVisualization,
    displayMode,
    setDisplayMode,
    limit,
    setLimit,
    preferences,
    setPreferences,
    metadata,
    metadataError,
    validation,
    result,
    runtimeMs,
    error,
    runQuery,
    insertAtCursor,
    dataBlocks,
    deleteDataBlock,
    updateDataBlockReferenceName,
    getDataBlockReferenceNameError,
    queryHistory,
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const referencedTableCount = useMemo(() => {
        const matches = code.matchAll(/\b(?:from|join)\s+("?[\w]+"?)/gi);
        const tableNames = new Set([...matches].map((match) => match[1].replace(/^"|"$/g, '').toLowerCase()));
        return metadata.tables.filter((table) => tableNames.has(table.name.toLowerCase())).length;
    }, [code, metadata.tables]);

    const restoreQuery = (entry: VantageQueryHistoryEntry | VantageDataBlock) => {
        setCode(entry.sql);
        setVisualization(entry.visualization);
        setDisplayMode(entry.displayMode);
        setLimit(entry.limit);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
            <Row
                style={{ padding: '1rem', borderBottom: `1px solid ${token.colorBorder}` }}
                wrap={false}
                align='middle'
                gutter={[8, 0]}
            >
                <Col flex='auto'>
                    <Typography.Title level={5} style={{ margin: 0 }}>
                        Edit Vantage Query
                    </Typography.Title>
                </Col>
                <Col flex='none'>
                    <Button
                        size='small'
                        onClick={() => setOpen(false)}
                        icon={<TbX style={{ color: token.colorTextTertiary }} />}
                    />
                </Col>
            </Row>
            <style>{`
                .vantage-query-settings-tabs { display: flex; flex: 1; flex-direction: column; min-height: 0; }
                .vantage-query-settings-tabs > .ant-tabs-nav { flex: none; margin: 0; padding: 0 1rem; }
                .vantage-query-settings-tabs > .ant-tabs-content-holder { min-height: 0; overflow-y: auto; }
                .vantage-query-settings-tabs > .ant-tabs-content-holder > .ant-tabs-content { min-height: 100%; }
                .vantage-query-settings-tabs .ant-tabs-tab-btn { align-items: center; display: inline-flex; gap: 0.375rem; }
            `}</style>
            <Tabs
                className='vantage-query-settings-tabs'
                size='small'
                items={[
                    {
                        key: 'preferences',
                        label: (
                            <>
                                <TbSettings /> Preferences
                            </>
                        ),
                        children: (
                            <PreferencesTab
                                displayMode={displayMode}
                                limit={limit}
                                preferences={preferences}
                                result={result}
                                validation={validation}
                                visualization={visualization}
                                runQuery={runQuery}
                                setDisplayMode={setDisplayMode}
                                setLimit={setLimit}
                                setPreferences={setPreferences}
                                setVisualization={setVisualization}
                            />
                        ),
                    },
                    {
                        key: 'templates',
                        label: (
                            <>
                                <TbFileText /> Templates
                            </>
                        ),
                        children: (
                            <QueryTemplatesTab
                                code={code}
                                limit={limit}
                                metadata={metadata}
                                metadataError={metadataError}
                                setCode={setCode}
                            />
                        ),
                    },
                    {
                        key: 'metadata',
                        label: (
                            <>
                                <TbDatabase /> Metadata
                            </>
                        ),
                        children: (
                            <QueriesTab
                                insertAtCursor={insertAtCursor}
                                limit={limit}
                                metadata={metadata}
                                metadataError={metadataError}
                                setCode={setCode}
                            />
                        ),
                    },
                    {
                        key: 'queries',
                        label: (
                            <>
                                <TbCode /> Queries
                            </>
                        ),
                        children: (
                            <QueryMetadataTab
                                code={code}
                                dataBlocks={dataBlocks}
                                deleteDataBlock={deleteDataBlock}
                                error={error}
                                metadata={metadata}
                                queryHistory={queryHistory}
                                referencedTableCount={referencedTableCount}
                                result={result}
                                runtimeMs={runtimeMs}
                                getDataBlockReferenceNameError={getDataBlockReferenceNameError}
                                restoreQuery={restoreQuery}
                                updateDataBlockReferenceName={updateDataBlockReferenceName}
                            />
                        ),
                    },
                ]}
            />
        </div>
    );
};

export default Settings;
